(() => {
  const selector = '[data-abris-cart-booking-datetime-value]';

  const isFrenchDocument = (doc) =>
    String(doc?.documentElement?.lang || '').trim().toLowerCase().startsWith('fr');

  const formatFrenchTimeText = (value) => {
    const source = String(value || '');
    if (!/\b(?:AM|PM)\b/i.test(source)) return source;

    const converted = source.replace(
      /\b(\d{1,2}):(\d{2})\s*(AM|PM)\b/gi,
      (_, hourText, minuteText, periodText) => {
        let hour = Number(hourText);
        const minutes = Number(minuteText);
        const period = String(periodText).toUpperCase();

        if (period === 'AM' && hour === 12) hour = 0;
        if (period === 'PM' && hour !== 12) hour += 12;

        return minutes === 0
          ? `${hour} h`
          : `${hour} h ${String(minutes).padStart(2, '0')}`;
      }
    );

    return converted.replace(/\s+-\s+/g, ' – ');
  };

  const MONTHS_FR = {
    janv: 0, fevr: 1, mars: 2, avr: 3, mai: 4, juin: 5,
    juill: 6, aout: 7, sept: 8, oct: 9, nov: 10, dec: 11,
  };

  const MONTHS_EN = {
    jan: 0, january: 0, feb: 1, february: 1, mar: 2, march: 2,
    apr: 3, april: 3, may: 4, jun: 5, june: 5, jul: 6, july: 6,
    aug: 7, august: 7, sep: 8, sept: 8, september: 8,
    oct: 9, october: 9, nov: 10, november: 10, dec: 11, december: 11,
  };

  const normalizeMonthToken = (value) =>
    String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\./g, '');

  const parseBookingDate = (value) => {
    const text = String(value || '');

    const french = text.match(/\b(\d{1,2})\s+([A-Za-zÀ-ÿ.]+)\s+(\d{4})\b/);
    if (french) {
      const month = MONTHS_FR[normalizeMonthToken(french[2])];
      if (Number.isInteger(month)) {
        return {
          date: new Date(Number(french[3]), month, Number(french[1])),
          match: french[0],
        };
      }
    }

    const english = text.match(/\b([A-Za-z.]+)\s+(\d{1,2}),\s*(\d{4})\b/);
    if (english) {
      const month = MONTHS_EN[normalizeMonthToken(english[1])];
      if (Number.isInteger(month)) {
        return {
          date: new Date(Number(english[3]), month, Number(english[2])),
          match: english[0],
        };
      }
    }

    return null;
  };

  const formatLocalizedDate = (value, french) => {
    const parsed = parseBookingDate(value);
    if (!parsed) return String(value || '');

    const locale = french ? 'fr-CA' : 'en-CA';
    const options = french
      ? { day: 'numeric', month: 'short', year: 'numeric' }
      : { month: 'short', day: 'numeric', year: 'numeric' };

    const formatted = new Intl.DateTimeFormat(locale, options)
      .format(parsed.date)
      .replace(/\s+/g, ' ')
      .trim();

    return String(value || '').replace(parsed.match, formatted);
  };

  const formatEnglishTimeText = (value) => {
    const source = String(value || '');

    const converted = source.replace(
      /\b(\d{1,2})\s*h(?:\s*(\d{2}))?\b/gi,
      (_, hourText, minuteText) => {
        const hour24 = Number(hourText);
        const minutes = minuteText ? Number(minuteText) : 0;
        if (!Number.isFinite(hour24) || hour24 < 0 || hour24 > 23) return _;

        const period = hour24 >= 12 ? 'PM' : 'AM';
        const hour12 = hour24 % 12 || 12;
        return `${hour12}:${String(minutes).padStart(2, '0')} ${period}`;
      }
    );

    return converted.replace(/\s+-\s+/g, ' – ');
  };

  const formatBookingDateTime = (value, french) => {
    let formatted = String(value || '');
    formatted = formatLocalizedDate(formatted, french);
    formatted = french ? formatFrenchTimeText(formatted) : formatEnglishTimeText(formatted);
    return formatted.replace(/\s+-\s+/g, ' – ');
  };

  const localizedPropertyName = (french) => french ? 'Date et horaire' : 'Date and time';

  const normalize = (root = document) => {
    if (!root?.querySelectorAll) return;

    const french = isFrenchDocument(document);
    const label = localizedPropertyName(french);

    root.querySelectorAll('.cart-items__property--booking-datetime').forEach((property) => {
      const term = property.querySelector('dt');
      const value = property.querySelector(selector);

      if (term && term.textContent !== `${label}:`) term.textContent = `${label}:`;
      if (!value) return;

      const current = value.textContent || '';
      const formatted = formatBookingDateTime(current, french);
      if (formatted !== current) value.textContent = formatted;
    });
  };

  const isBookingDateTimeProperty = (name, value) => {
    const key = String(name || '').trim().toLowerCase();
    const text = String(value || '');
    if (key === 'date et horaire' || key === 'date and time') return true;
    return key.includes('date') && /\b(?:AM|PM)\b/i.test(text);
  };

  let storedSyncRunning = false;
  let storedSyncQueued = false;
  let storedSyncTimer = 0;

  const cartRoot = () => String(window.Shopify?.routes?.root || '/');

  const normalizeStoredCart = async () => {
    const french = isFrenchDocument(document);
    const targetName = localizedPropertyName(french);

    if (storedSyncRunning) {
      storedSyncQueued = true;
      return;
    }

    storedSyncRunning = true;

    try {
      const response = await fetch(cartRoot() + 'cart.js', {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store',
      });
      if (!response.ok) return;

      const cart = await response.json();
      const items = Array.isArray(cart?.items) ? cart.items : [];

      for (let index = 0; index < items.length; index += 1) {
        const item = items[index];
        const properties = item?.properties && typeof item.properties === 'object'
          ? { ...item.properties }
          : null;

        if (!properties) continue;

        const bookingEntries = Object.entries(properties)
          .filter(([name, value]) => isBookingDateTimeProperty(name, value));

        if (!bookingEntries.length) continue;

        const [, sourceValue] = bookingEntries[0];
        const formatted = formatBookingDateTime(sourceValue, french);
        let changed = false;

        bookingEntries.forEach(([name]) => {
          if (name === targetName) return;
          delete properties[name];
          changed = true;
        });

        if (properties[targetName] !== formatted) {
          properties[targetName] = formatted;
          changed = true;
        }

        if (!changed) continue;

        const changeResponse = await fetch(cartRoot() + 'cart/change.js', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          credentials: 'same-origin',
          body: JSON.stringify({
            line: index + 1,
            properties,
          }),
        });

        if (!changeResponse.ok) {
          console.warn('[abris-cart-booking] Could not localize booking date/time in cart.');
        }
      }
    } catch (error) {
      console.warn('[abris-cart-booking] Cart date/time localization failed:', error);
    } finally {
      storedSyncRunning = false;
      if (storedSyncQueued) {
        storedSyncQueued = false;
        queueStoredSync(80);
      }
    }
  };

  const queueStoredSync = (delay = 120) => {
    clearTimeout(storedSyncTimer);
    storedSyncTimer = window.setTimeout(normalizeStoredCart, delay);
  };

  let frame = 0;
  const queue = () => {
    if (frame) return;
    frame = requestAnimationFrame(() => {
      frame = 0;
      normalize(document);
      queueStoredSync();
    });
  };

  const start = () => {
    normalize(document);
    queueStoredSync(0);

    const observer = new MutationObserver(queue);
    const roots = [
      document.getElementById('cart-drawer'),
      document.querySelector('#MainContent'),
    ].filter(Boolean);

    roots.forEach((root) => {
      observer.observe(root, {
        subtree: true,
        childList: true,
        characterData: true,
      });
    });

    document.addEventListener('abris:cart-synced', () => {
      queue();
      queueStoredSync(0);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();